import { type FC, useEffect } from 'react'

import { IdleExpandedCustomMessage } from '@liveHelper/components/Idle/IdleExpandedCustomMessage'
import { IdleExpandedOfflineTimeSlots } from '@liveHelper/components/Idle/IdleExpandedOfflineTimeSlots'
import { IdleExpandedScheduleOnly } from '@liveHelper/components/Idle/IdleExpandedScheduleOnly'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { QueuePosition } from '@liveHelper/components/shared/QueuePosition'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import {
  actionsSelector,
  queuePositionSelector,
  useStore,
  waitlistCapacityReachedSelector,
} from '@liveHelper/models/store'
import { useTrackIdleImpression } from '@liveHelper/tracking/useLiveHelperTracking'

import { ImpressionContainer } from '@core/components/ImpressionContainer'

import { IdleExpanded } from './IdleExpanded'
import { IdleFoldedAnimated } from './IdleFoldedAnimated'
import { IdleFoldedAvatars } from './IdleFoldedAvatars'

/**
 * Agent widget "idle" state component.
 */
export const Idle: FC<{
  isExpanded: boolean
}> = ({ isExpanded }) => {
  const chatChannelConfig = useChatChannelConfig()
  const queuePosition = useStore(queuePositionSelector)
  const waitlistCapacityReached = useStore(waitlistCapacityReachedSelector)

  const isOffline = useIsOffline()
  const { lastUnreadMessage } = useMessagePreviewContext()
  const trackIdleImpression = useTrackIdleImpression()
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  useEffect(() => {
    if (isExpanded && lastUnreadMessage) {
      sendUIStateTransitionEvent('START_CHAT')
    }
  }, [isExpanded, lastUnreadMessage, sendUIStateTransitionEvent])

  if (!isExpanded) {
    if (queuePosition) {
      return <QueuePosition queuePosition={queuePosition} />
    }

    return (
      <ImpressionContainer
        enabled={!lastUnreadMessage}
        trackingFn={trackIdleImpression}
      >
        {chatChannelConfig?.widget_style?.icon_style === 'avatar' ? (
          <IdleFoldedAvatars />
        ) : (
          <IdleFoldedAnimated />
        )}
      </ImpressionContainer>
    )
  }

  if (isOffline) {
    return <IdleExpandedOfflineTimeSlots />
  }

  if (waitlistCapacityReached) {
    const action =
      chatChannelConfig?.config?.waitlist_options?.on_max_capacity_action

    if (action === 'message') {
      return <IdleExpandedCustomMessage />
    }

    if (action === 'schedule') {
      return <IdleExpandedScheduleOnly />
    }
  }

  return <IdleExpanded />
}
